import { createSlice } from '@reduxjs/toolkit'

/**
 * Sync slice - manages sync-specific state for multi-layout synchronization
 */
export const syncSlice = createSlice({
    name: 'sync',
    initialState: {
        // Current LLM plan awaiting approval (Smart mode)
        pendingPlan: null,

        // Elements shown in ghost preview
        ghostPreviewElements: [],

        // Elements to toggle in ghost preview
        ghostPreviewToggles: {}, // globalId -> boolean

        // Missing elements (createSuggestions from LLM)
        missingElements: [],

        // Whether to show ghost preview modal
        isGhostPreviewOpen: false,

        // Whether to show missing elements modal
        isMissingElementsModalOpen: false,

        // Current sync operation status
        syncStatus: 'idle', // 'idle' | 'pending' | 'applying' | 'completed' | 'error'

        // Last sync error
        syncError: null,

        // Telemetry log for sync operations
        syncRunLog: [],

        // LLM confidence from last Smart mode run
        lastConfidence: null,

        // Source layout ID for current sync operation
        sourceLayoutId: null,

        // Target layout IDs for current sync operation
        targetLayoutIds: [],
    },
    reducers: {
        // Ghost Preview
        setPendingPlan: (state, action) => {
            state.pendingPlan = action.payload
        },
        setGhostPreviewElements: (state, action) => {
            state.ghostPreviewElements = action.payload
        },
        toggleGhostPreviewElement: (state, action) => {
            const globalId = action.payload
            state.ghostPreviewToggles[globalId] = !state.ghostPreviewToggles[globalId]
        },
        setGhostPreviewToggle: (state, action) => {
            const { globalId, enabled } = action.payload
            state.ghostPreviewToggles[globalId] = enabled
        },
        clearGhostPreview: (state) => {
            state.ghostPreviewElements = []
            state.ghostPreviewToggles = {}
            state.pendingPlan = null
            state.isGhostPreviewOpen = false
        },
        setGhostPreviewOpen: (state, action) => {
            state.isGhostPreviewOpen = action.payload
        },

        // Missing Elements
        setMissingElements: (state, action) => {
            state.missingElements = action.payload
        },
        clearMissingElements: (state) => {
            state.missingElements = []
            state.isMissingElementsModalOpen = false
        },
        setMissingElementsModalOpen: (state, action) => {
            state.isMissingElementsModalOpen = action.payload
        },

        // Sync Status
        setSyncStatus: (state, action) => {
            state.syncStatus = action.payload
        },
        setSyncError: (state, action) => {
            state.syncError = action.payload
            state.syncStatus = 'error'
        },
        clearSyncError: (state) => {
            state.syncError = null
            state.syncStatus = 'idle'
        },

        // LLM Confidence
        setLastConfidence: (state, action) => {
            state.lastConfidence = action.payload
        },

        // Layout context
        setSourceLayoutId: (state, action) => {
            state.sourceLayoutId = action.payload
        },
        setTargetLayoutIds: (state, action) => {
            state.targetLayoutIds = action.payload
        },

        // Telemetry
        addSyncRunLog: (state, action) => {
            state.syncRunLog.push({
                ...action.payload,
                timestamp: Date.now(),
            })
            // Keep only last 100 entries
            if (state.syncRunLog.length > 100) {
                state.syncRunLog = state.syncRunLog.slice(-100)
            }
        },
        clearSyncRunLog: (state) => {
            state.syncRunLog = []
        },

        // Reset all sync state
        resetSyncState: (state) => {
            state.pendingPlan = null
            state.ghostPreviewElements = []
            state.ghostPreviewToggles = {}
            state.missingElements = []
            state.isGhostPreviewOpen = false
            state.isMissingElementsModalOpen = false
            state.syncStatus = 'idle'
            state.syncError = null
            state.lastConfidence = null
            state.sourceLayoutId = null
            state.targetLayoutIds = []
        },
    },
})

export const {
    setPendingPlan,
    setGhostPreviewElements,
    toggleGhostPreviewElement,
    setGhostPreviewToggle,
    clearGhostPreview,
    setGhostPreviewOpen,
    setMissingElements,
    clearMissingElements,
    setMissingElementsModalOpen,
    setSyncStatus,
    setSyncError,
    clearSyncError,
    setLastConfidence,
    setSourceLayoutId,
    setTargetLayoutIds,
    addSyncRunLog,
    clearSyncRunLog,
    resetSyncState,
} = syncSlice.actions

export default syncSlice.reducer
